import { fetchMailAttachmentBlob } from "@/lib/api/imap";
import type { ComposeAttachment, ComposeAttachmentPayload, ComposeDraft } from "@/lib/types/compose";
import type { MailAttachment } from "@/lib/types/mail";

export const COMPOSE_ATTACHMENT_MAX_FILES = 10;
export const COMPOSE_ATTACHMENT_MAX_FILE_BYTES = 20 * 1024 * 1024;
export const COMPOSE_ATTACHMENT_MAX_TOTAL_BYTES = 25 * 1024 * 1024;

export function formatComposeAttachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

/** Fichiers issus d’un drop / collage navigateur. */
export function filesFromDataTransfer(data: DataTransfer | null | undefined): File[] {
  if (!data) return [];
  if (data.files?.length) {
    return [...data.files].filter((file) => file.name || file.size > 0);
  }
  const out: File[] = [];
  for (const item of data.items ?? []) {
    if (item.kind !== "file") continue;
    const file = item.getAsFile();
    if (file && (file.name || file.size > 0)) out.push(file);
  }
  return out;
}

export function dataTransferHasFiles(data: DataTransfer | null | undefined): boolean {
  if (!data) return false;
  if (data.files && data.files.length > 0) return true;
  try {
    return [...data.types].includes("Files");
  } catch {
    return false;
  }
}

function readBlobAsBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Lecture du fichier impossible."));
        return;
      }
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Lecture du fichier impossible."));
    reader.readAsDataURL(blob);
  });
}

export function composeAttachmentsTotalBytes(attachments: ComposeAttachment[]): number {
  return attachments.reduce((sum, file) => sum + file.size, 0);
}

export async function filesToComposeAttachments(
  files: FileList | File[],
  existing: ComposeAttachment[] = []
): Promise<{ attachments: ComposeAttachment[]; error?: string }> {
  const list = [...files];
  if (list.length === 0) {
    return { attachments: existing };
  }

  const next = [...existing];
  let total = composeAttachmentsTotalBytes(next);

  for (const file of list) {
    if (next.length >= COMPOSE_ATTACHMENT_MAX_FILES) {
      return { attachments: next, error: `Maximum ${COMPOSE_ATTACHMENT_MAX_FILES} pièces jointes.` };
    }
    if (file.size <= 0) {
      return {
        attachments: next,
        error: `${file.name || "Fichier"} est vide (0 o) et ne peut pas être joint.`,
      };
    }
    if (file.size > COMPOSE_ATTACHMENT_MAX_FILE_BYTES) {
      return {
        attachments: next,
        error: `${file.name} dépasse ${formatComposeAttachmentSize(COMPOSE_ATTACHMENT_MAX_FILE_BYTES)}.`,
      };
    }
    if (total + file.size > COMPOSE_ATTACHMENT_MAX_TOTAL_BYTES) {
      return { attachments: next, error: "Taille totale des pièces jointes limitée à 25 Mo." };
    }

    const contentBase64 = await readBlobAsBase64(file);
    next.push({
      id: crypto.randomUUID(),
      filename: file.name || "piece-jointe",
      mimeType: file.type || "application/octet-stream",
      size: file.size,
      contentBase64,
    });
    total += file.size;
  }

  return { attachments: next };
}

/** Ajoute des fichiers déjà téléchargés (Coffre, etc.). */
export async function appendFilesToComposeAttachments(
  files: File[],
  existing: ComposeAttachment[] = []
): Promise<{ attachments: ComposeAttachment[]; error?: string }> {
  if (files.length === 0) return { attachments: existing };
  return filesToComposeAttachments(files, existing);
}

export function composeAttachmentIdFromMail(file: MailAttachment): string {
  return `imap:${file.part}:${file.filename}`;
}

/** Affiche tout de suite les PJ connues pendant le téléchargement du contenu. */
export function mailAttachmentsToComposeStubs(attachments: MailAttachment[]): ComposeAttachment[] {
  return attachments.map((file) => ({
    id: composeAttachmentIdFromMail(file),
    filename: file.filename,
    mimeType: file.mime || "application/octet-stream",
    size: file.size,
    contentBase64: "",
    loading: true,
  }));
}

export function composeAttachmentsLoading(attachments: ComposeAttachment[] | undefined): boolean {
  return attachments?.some((file) => file.loading) ?? false;
}

/** Recharge les PJ d’un brouillon IMAP pour réédition / ré-enregistrement. */
export async function mailAttachmentsToComposeAttachments(
  accountId: number,
  folder: string,
  uid: number,
  attachments: MailAttachment[]
): Promise<ComposeAttachment[]> {
  if (attachments.length === 0) return [];

  const results = await Promise.all(
    attachments.map(async (file): Promise<ComposeAttachment | null> => {
      try {
        const blob = await fetchMailAttachmentBlob({
          accountId,
          folder,
          uid,
          part: file.part,
          filename: file.filename,
        });
        const contentBase64 = await readBlobAsBase64(blob);
        return {
          id: composeAttachmentIdFromMail(file),
          filename: file.filename,
          mimeType: file.mime || blob.type || "application/octet-stream",
          size: file.size > 0 ? file.size : blob.size,
          contentBase64,
        };
      } catch {
        return null;
      }
    })
  );

  return results.filter((file): file is ComposeAttachment => file != null);
}

export function payloadAttachmentsToCompose(
  attachments?: ComposeAttachmentPayload[]
): ComposeAttachment[] | undefined {
  if (!attachments?.length) return undefined;
  return attachments.map((file) => ({
    id: crypto.randomUUID(),
    filename: file.filename,
    mimeType: file.mimeType,
    size: Math.max(0, Math.ceil((file.contentBase64.length * 3) / 4)),
    contentBase64: file.contentBase64,
  }));
}

export function attachmentsForApi(attachments: ComposeAttachment[] | undefined) {
  if (!attachments?.length) return undefined;
  const ready = attachments.filter((file) => !file.loading && file.contentBase64);
  if (ready.length === 0) return undefined;
  return ready.map(({ filename, mimeType, contentBase64 }) => ({
    filename,
    mimeType,
    contentBase64,
  }));
}

export function stripAttachmentsForStorage(draft: ComposeDraft): ComposeDraft {
  if (!draft.attachments?.length) return draft;
  const { attachments: _ignored, ...rest } = draft;
  return rest;
}
